import React from 'react';
import { useParams } from 'react-router-dom';
import { Heading, Subheading } from '../catalyst/heading';
import { Button } from '../catalyst/button';
import {
	DescriptionList,
	DescriptionTerm,
	DescriptionDetails,
} from '../catalyst/description-list';
import {
	Table,
	TableHead,
	TableBody,
	TableRow,
	TableHeader,
	TableCell,
} from '../catalyst/table';
import { Badge } from '../catalyst/badge';
import { Divider } from '../catalyst/divider';
import { Link } from '../catalyst/link';
import { OrderStatusBadge, PaymentStatusBadge, ProdigiStatusBadge, LobStatusBadge } from '../components/StatusBadge';
import { useOrder, useOrderSlots } from '../hooks/useOrders';
import { usePaymentIntents } from '../hooks/usePayments';
import { useProdigiOrders, useLobOrders } from '../hooks/useFulfillment';
import { useLedgerTransactions } from '../hooks/useCodes';
import { useSupabaseRow, useSupabaseQuery } from '../hooks/useSupabaseQuery';
import { REQUIRED_SLOTS } from '../types/database';
import type { OrderStatus, OrderSlot, User, Asset, LobStatus } from '../types/database';
import { ArrowLeft, Check, X, MessageSquare } from 'lucide-react';
import ImageThumbnail from '../components/ImageThumbnail';
import ResourceLink from '../components/ResourceLink';

const SLOT_LABELS: Record<string, string> = {
	front_image_asset_id: 'Front Image',
	message_text: 'Message Text',
	recipient_name: 'Recipient Name',
	recipient_address_validated: 'Recipient Address',
	product_variant: 'Product Variant',
	price_amount: 'Price Amount',
	currency: 'Currency',
	print_assets_ready: 'Print Assets Ready',
};

// -- Step progress constants --
const ORDER_STEPS: { key: OrderStatus; label: string }[] = [
	{ key: 'DRAFT', label: 'Draft' },
	{ key: 'IN_REVIEW', label: 'In Review' },
	{ key: 'PENDING_PAYMENT', label: 'Payment' },
	{ key: 'PAID', label: 'Paid' },
	{ key: 'FULFILLING', label: 'Fulfilling' },
	{ key: 'SHIPPED', label: 'Shipped' },
	{ key: 'DELIVERED', label: 'Delivered' },
];

const STEP_INDEX: Record<string, number> = {
	DRAFT: 0,
	IN_REVIEW: 1,
	PENDING_PAYMENT: 2,
	PAID: 3,
	FULFILLING: 4,
	SHIPPED: 5,
	DELIVERED: 6,
};

// -- Humanize product variant SKU --
const SKU_SEGMENTS: Record<string, string> = {
	CLASSIC: 'Classic',
	POST: 'Postcard',
	GLOS: 'Gloss',
	MAT: 'Matte',
	'6X4': '6×4"',
	'4X6': '4×6"',
	PREM: 'Premium',
	STD: 'Standard',
};

function humanizeSku(raw: string): string {
	const parts = raw.split('-');
	const mapped = parts.map((p) => SKU_SEGMENTS[p.toUpperCase()] || p);
	// If at least one segment was recognized, return humanized; otherwise fallback
	if (mapped.some((m, i) => m !== parts[i])) {
		return mapped.join(' · ');
	}
	return raw;
}

// -- Time formatting --
function formatTimeAgo(ms: number): string {
	if (ms < 60_000) return 'just now';
	const minutes = Math.floor(ms / 60_000);
	if (minutes < 60) return `${minutes} minute${minutes === 1 ? '' : 's'}`;
	const hours = Math.floor(minutes / 60);
	if (hours < 24) return `${hours} hour${hours === 1 ? '' : 's'}`;
	const days = Math.floor(hours / 24);
	return `${days} day${days === 1 ? '' : 's'}`;
}

// -- Slot value formatter --
function formatSlotValue(
	slotName: string,
	value: Record<string, unknown> | null,
	allSlots: Map<string, Record<string, unknown> | null>,
): string {
	if (value === null || value === undefined) return '—';

	switch (slotName) {
		case 'print_assets_ready': {
			if (value.ready === true) return '✓ Ready';
			return 'Pending';
		}

		case 'price_amount': {
			const raw = value.amount ?? value.value ?? Object.values(value)[0];
			const num = typeof raw === 'number' ? raw : Number(raw);
			if (isNaN(num)) return JSON.stringify(value);
			const currencySlot = allSlots.get('currency');
			if (currencySlot) {
				const code = (currencySlot.code ?? currencySlot.currency ?? Object.values(currencySlot)[0]) as string;
				if (code && typeof code === 'string') {
					try {
						return new Intl.NumberFormat('en-US', {
							style: 'currency',
							currency: code.toUpperCase(),
						}).format(num / 100);
					} catch {
						// invalid currency code — fallback
					}
				}
			}
			return (num / 100).toFixed(2);
		}

		case 'front_image_asset_id': {
			const assetId = value.asset_id ?? value.id ?? Object.values(value)[0];
			if (assetId && typeof assetId === 'string') {
				return `Image linked (${assetId})`;
			}
			return 'Image linked';
		}

		case 'product_variant': {
			const sku = (value.variant ?? value.sku ?? value.product_variant ?? Object.values(value)[0]) as string;
			if (sku && typeof sku === 'string') return humanizeSku(sku);
			return JSON.stringify(value);
		}

		case 'recipient_address_validated': {
			if (typeof value === 'object') {
				// Prefer formatted_address if available
				if ('formatted_address' in value && typeof value.formatted_address === 'string') {
					return value.formatted_address;
				}
				const parts: string[] = [];
				const addr = value as Record<string, unknown>;
				if (addr.street) parts.push(String(addr.street));
				if (addr.address_line_1) parts.push(String(addr.address_line_1));
				if (addr.address_line_2) parts.push(String(addr.address_line_2));
				if (addr.city) parts.push(String(addr.city));
				if (addr.state || addr.province) parts.push(String(addr.state || addr.province));
				if (addr.postal_code || addr.zip) parts.push(String(addr.postal_code || addr.zip));
				if (addr.country) parts.push(String(addr.country));
				if (parts.length > 0) return parts.join(', ');
			}
			return JSON.stringify(value);
		}

		case 'message_text': {
			if ('text' in value && typeof value.text === 'string') return value.text;
			const keys = Object.keys(value);
			if (keys.length === 1) {
				const v = value[keys[0]];
				if (typeof v === 'string') return v;
			}
			return JSON.stringify(value);
		}

		case 'recipient_name': {
			if ('name' in value && typeof value.name === 'string') return value.name;
			const keys = Object.keys(value);
			if (keys.length === 1) {
				const v = value[keys[0]];
				if (typeof v === 'string') return v;
			}
			return JSON.stringify(value);
		}

		case 'currency': {
			const code = (value.code ?? value.currency ?? Object.values(value)[0]) as string;
			if (code && typeof code === 'string') return code.toUpperCase();
			return JSON.stringify(value);
		}

		default: {
			// Generic: single-key shortcut
			const keys = Object.keys(value);
			if (keys.length === 1) {
				const v = value[keys[0]];
				if (typeof v === 'string' || typeof v === 'number' || typeof v === 'boolean') {
					return String(v);
				}
			}
			if ('text' in value && typeof value.text === 'string') return value.text;
			return JSON.stringify(value);
		}
	}
}

function formatDate(dateStr: string): string {
	return new Date(dateStr).toLocaleDateString('en-US', {
		month: 'short',
		day: 'numeric',
		year: 'numeric',
		hour: '2-digit',
		minute: '2-digit',
	});
}

function formatCents(amount: number, currency: string): string {
	return new Intl.NumberFormat('en-US', {
		style: 'currency',
		currency: currency.toUpperCase(),
	}).format(amount / 100);
}

function formatEntryType(entryType: string): string {
	return entryType
		.toLowerCase()
		.replace(/_/g, ' ')
		.replace(/\b\w/g, (char) => char.toUpperCase());
}

// -- Build slot lookup map --
function buildSlotMap(slots: OrderSlot[]): Map<string, Record<string, unknown> | null> {
	const map = new Map<string, Record<string, unknown> | null>();
	for (const slot of slots) {
		map.set(slot.slot_name, slot.value);
	}
	return map;
}

// -- Order Progress Indicator --
function OrderProgress({ status }: { status: OrderStatus }) {
	const isCancelled = status === 'CANCELLED';
	const currentIdx = STEP_INDEX[status] ?? -1;

	return (
		<div className="flex items-start justify-between gap-0 overflow-x-auto py-2">
			{ORDER_STEPS.map((step, idx) => {
				const isCompleted = !isCancelled && idx < currentIdx;
				const isCurrent = !isCancelled && idx === currentIdx;

				return (
					<React.Fragment key={step.key}>
						<div className="flex flex-col items-center" style={{ minWidth: 64 }}>
							{/* Circle */}
							<div className="relative flex items-center justify-center">
								{isCurrent && (
									<span className="absolute inline-flex h-8 w-8 animate-ping rounded-full bg-blue-400 opacity-25" />
								)}
								<div
									className={`relative z-10 flex h-8 w-8 items-center justify-center rounded-full text-xs font-semibold ${
										isCompleted
											? 'bg-green-500 text-white'
											: isCurrent
												? 'bg-blue-600 text-white ring-2 ring-blue-300'
												: 'bg-zinc-200 text-zinc-500 dark:bg-zinc-700 dark:text-zinc-400'
									}`}
								>
									{isCompleted ? (
										<Check className="h-4 w-4" />
									) : (
										idx + 1
									)}
								</div>
							</div>
							{/* Label */}
							<span
								className={`mt-1.5 text-center text-xs leading-tight ${
									isCompleted
										? 'font-medium text-green-700 dark:text-green-400'
										: isCurrent
											? 'font-semibold text-blue-700 dark:text-blue-400'
											: 'text-zinc-400 dark:text-zinc-500'
								}`}
							>
								{step.label}
							</span>
						</div>
						{/* Connector line */}
						{idx < ORDER_STEPS.length - 1 && (
							<div className="mt-4 flex flex-1 items-center px-1" style={{ minWidth: 16 }}>
								<div
									className={`h-0.5 w-full ${
										!isCancelled && idx < currentIdx
											? 'bg-green-500'
											: 'bg-zinc-200 dark:bg-zinc-700'
									}`}
								/>
							</div>
						)}
					</React.Fragment>
				);
			})}
			{/* Cancelled badge */}
			{isCancelled && (
				<div className="ml-4 flex flex-col items-center" style={{ minWidth: 64 }}>
					<div className="flex h-8 w-8 items-center justify-center rounded-full bg-red-500 text-white">
						<X className="h-4 w-4" />
					</div>
					<Badge color="red" className="mt-1.5">Cancelled</Badge>
				</div>
			)}
		</div>
	);
}

const ASSET_TYPE_LABELS: Record<string, string> = {
	USER_UPLOAD: 'User Upload',
	FRONT_PRINT: 'Front Print',
	BACK_PRINT: 'Back Print',
	PREVIEW: 'Preview',
	GENERATED: 'Generated',
};

function OrderAssetsGrid({ assets }: { assets: Asset[] }) {
	if (assets.length === 0) return null;
	return (
		<>
			<Divider className="my-6" />
			<section className="mb-6">
				<Subheading className="mb-4">Assets ({assets.length})</Subheading>
				<div className="grid grid-cols-2 gap-4 sm:grid-cols-3 md:grid-cols-4">
					{assets.map((asset) => (
						<div key={asset.id} className="flex flex-col items-center gap-2">
						<ImageThumbnail assetId={asset.id} alt={asset.asset_type} height={120} className="rounded-lg" />
							<span className="text-xs font-medium text-zinc-500">
								{ASSET_TYPE_LABELS[asset.asset_type] || asset.asset_type}
							</span>
						</div>
					))}
				</div>
			</section>
		</>
	);
}
export default function OrderDetailPage() {
	const { id } = useParams<{ id: string }>();
	const { data: order, loading: orderLoading, error: orderError } = useOrder(id);
	const { data: slots, loading: slotsLoading } = useOrderSlots(id);
	const { data: payments, loading: paymentsLoading } = usePaymentIntents(id);
	const { data: prodigiOrders, loading: prodigiLoading } = useProdigiOrders(id);
	const { data: lobOrders, loading: lobLoading } = useLobOrders(id);
	const { data: user } = useSupabaseRow<User>('users', order?.user_id);
	const { data: assets } = useSupabaseQuery<Asset>(
		(client) =>
			client
				.from('assets')
				.select('*')
				.eq('order_id', id ?? '')
				.order('created_at', { ascending: true }),
		[id],
	);
	const { data: ledgerTransactions } = useLedgerTransactions({ orderId: id });

	if (orderLoading) {
		return <div className="py-12 text-center text-zinc-500">Loading order...</div>;
	}

	if (orderError) {
		return (
			<div className="py-12 text-center">
				<p className="text-red-600 dark:text-red-400 mb-4">{orderError}</p>
				<Button href="/orders">Back to Orders</Button>
			</div>
		);
	}

	if (!order) {
		return (
			<div className="py-12 text-center">
				<p className="text-zinc-500 mb-4">Order not found.</p>
				<Button href="/orders">Back to Orders</Button>
			</div>
		);
	}

	const requiredSlotNames = new Set<string>(REQUIRED_SLOTS);
	const slotMap = buildSlotMap(slots ?? []);

	// Time in status
	const timeInStatus = Date.now() - new Date(order.updated_at).getTime();
	const timeInStatusStr = formatTimeAgo(timeInStatus);

	// Preview asset
	const previewAsset = assets.find((a) => a.asset_type === 'PREVIEW');

	return (
		<div>
			{/* Header */}
			<div className="mb-6">
				<Link href="/orders" className="inline-flex items-center gap-1 text-sm text-zinc-500 hover:text-zinc-700 dark:hover:text-zinc-300 mb-4">
					<ArrowLeft className="h-4 w-4" />
					Back to Orders
				</Link>
				<div className="flex flex-wrap items-center gap-4">
					<Heading>Order {order.id}</Heading>
					<OrderStatusBadge status={order.status} />
					<span className="text-sm text-zinc-500">for {timeInStatusStr}</span>
				</div>
				<div className="mt-2 flex flex-wrap gap-4 text-sm text-zinc-500">
					<span>Created: {formatDate(order.created_at)}</span>
					<span>Updated: {formatDate(order.updated_at)}</span>
				</div>
			</div>

			<Divider className="my-6" />

			{/* User Summary */}
			<section className="mb-6">
				<Subheading className="mb-4">Customer</Subheading>
				{user ? (
					<DescriptionList>
						<DescriptionTerm>Name</DescriptionTerm>
						<DescriptionDetails>{user.display_name || '—'}</DescriptionDetails>
						<DescriptionTerm>Phone</DescriptionTerm>
						<DescriptionDetails>{user.phone_number}</DescriptionDetails>
						<DescriptionTerm>Email</DescriptionTerm>
						<DescriptionDetails>{user.email || '—'}</DescriptionDetails>
						<DescriptionTerm>User ID</DescriptionTerm>
						<DescriptionDetails>
							<ResourceLink
								id={user.id}
								href={`/users/${user.id}`}
							/>
						</DescriptionDetails>
					</DescriptionList>
				) : (
					<p className="text-sm text-zinc-500">Loading user...</p>
				)}
			</section>

			{/* Attribution & Pricing */}
			{((order as any).first_code_id || (order as any).discount_code_id || (order as any).subtotal_cash_cents != null) && (
				<>
					<Divider className="my-6" />
					<section className="mb-6">
						<Subheading className="mb-4">Attribution & Pricing</Subheading>
						<div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
							{/* Attribution */}
							<div>
								<p className="mb-2 text-sm font-medium text-zinc-500">Attribution</p>
								<DescriptionList>
									<DescriptionTerm>First Touch Code</DescriptionTerm>
									<DescriptionDetails>
										{(order as any).first_code_id
											? <ResourceLink id={(order as any).first_code_id} href={`/codes/${(order as any).first_code_id}`} />
											: '—'}
									</DescriptionDetails>
									<DescriptionTerm>Last Touch Code</DescriptionTerm>
									<DescriptionDetails>
										{(order as any).last_code_id
											? <ResourceLink id={(order as any).last_code_id} href={`/codes/${(order as any).last_code_id}`} />
											: '—'}
									</DescriptionDetails>
									<DescriptionTerm>Discount Code</DescriptionTerm>
									<DescriptionDetails>
										{(order as any).discount_code_id
											? <ResourceLink id={(order as any).discount_code_id} href={`/codes/${(order as any).discount_code_id}`} />
											: '—'}
									</DescriptionDetails>
									<DescriptionTerm>Payout Code</DescriptionTerm>
									<DescriptionDetails>
										{(order as any).payout_code_id
											? <ResourceLink id={(order as any).payout_code_id} href={`/codes/${(order as any).payout_code_id}`} />
											: '—'}
									</DescriptionDetails>
								</DescriptionList>
							</div>
							{/* Pricing */}
							<div>
								<p className="mb-2 text-sm font-medium text-zinc-500">Pricing</p>
								<DescriptionList>
									<DescriptionTerm>Subtotal</DescriptionTerm>
									<DescriptionDetails>{(order as any).subtotal_cash_cents != null ? `€${(Number((order as any).subtotal_cash_cents) / 100).toFixed(2)}` : '—'}</DescriptionDetails>
									<DescriptionTerm>Discount</DescriptionTerm>
									<DescriptionDetails>{(order as any).discount_cash_cents ? `−€${(Number((order as any).discount_cash_cents) / 100).toFixed(2)}` : '—'}</DescriptionDetails>
									<DescriptionTerm>Total</DescriptionTerm>
									<DescriptionDetails className="font-semibold">{(order as any).payable_cash_cents != null ? `€${(Number((order as any).payable_cash_cents) / 100).toFixed(2)}` : '—'}</DescriptionDetails>
									<DescriptionTerm>Fulfillment Cost</DescriptionTerm>
									<DescriptionDetails>{(order as any).fulfillment_cost != null ? `€${(Number((order as any).fulfillment_cost) / 100).toFixed(2)}` : '—'}</DescriptionDetails>
									<DescriptionTerm>Payment Cost</DescriptionTerm>
									<DescriptionDetails>{(order as any).payment_cost != null ? `€${(Number((order as any).payment_cost) / 100).toFixed(2)}` : '—'}</DescriptionDetails>
								</DescriptionList>
							</div>
						</div>
					</section>
				</>
			)}

			{/* Referral Debug */}
			{Array.isArray((order as any).referral_log) && (order as any).referral_log.length > 0 && (
				<>
					<Divider className="my-6" />
					<section className="mb-6">
						<Subheading className="mb-4">Referral Debug</Subheading>
						<Table dense>
							<TableHead>
								<TableRow>
									<TableHeader>Step</TableHeader>
									<TableHeader>Details</TableHeader>
									<TableHeader>Status</TableHeader>
								</TableRow>
							</TableHead>
							<TableBody>
								{((order as any).referral_log as Record<string, unknown>[]).map((entry, idx) => {
									const step = String(entry.step || 'unknown');
									const passed = entry.passed === true || entry.matched === true || entry.ledger_txn_created === true;
									const failed = entry.passed === false || entry.matched === false;
									const isResult = step === 'result' || step === 'payout_created';
									const isSuccess = step === 'payout_created';

									// Build detail string from remaining keys
									const detailParts: string[] = [];
									for (const [k, v] of Object.entries(entry)) {
										if (['step', 'passed', 'matched', 'ledger_txn_created'].includes(k)) continue;
										detailParts.push(`${k}: ${v === null ? 'null' : String(v)}`);
									}
									const details = detailParts.join(', ');

									let badgeColor: 'green' | 'red' | 'blue' | 'zinc' = 'zinc';
									let badgeText = '—';
									if (isSuccess) {
										badgeColor = 'green'; badgeText = 'Payout created';
									} else if (isResult) {
										badgeColor = 'red'; badgeText = String(entry.outcome || 'skipped');
									} else if (passed) {
										badgeColor = 'green'; badgeText = 'Pass';
									} else if (failed) {
										badgeColor = 'red'; badgeText = 'Fail';
									} else {
										badgeColor = 'blue'; badgeText = 'Info';
									}

									return (
										<TableRow key={idx}>
											<TableCell className="font-medium">{step.replace(/_/g, ' ')}</TableCell>
											<TableCell className="max-w-sm truncate text-sm text-zinc-500">{details || '—'}</TableCell>
											<TableCell><Badge color={badgeColor}>{badgeText}</Badge></TableCell>
										</TableRow>
									);
								})}
							</TableBody>
						</Table>
					</section>
				</>
			)}

			{ledgerTransactions.length > 0 && (
				<>
					<Divider className="my-6" />
					<section className="mb-6">
						<Subheading className="mb-4">Ledger Transactions</Subheading>
						<Table>
							<TableHead>
								<TableRow>
									<TableHeader>Type</TableHeader>
									<TableHeader>Currency</TableHeader>
									<TableHeader>Amount</TableHeader>
									<TableHeader>User</TableHeader>
									<TableHeader>Reason</TableHeader>
									<TableHeader>Created</TableHeader>
								</TableRow>
							</TableHead>
							<TableBody>
								{ledgerTransactions.map((t) => (
									<TableRow key={t.id}>
										<TableCell>
											<Badge>{formatEntryType(t.entry_type)}</Badge>
										</TableCell>
										<TableCell>{t.currency.toUpperCase()}</TableCell>
										<TableCell className={t.amount < 0 ? 'text-red-600' : 'text-green-600'}>
											{t.amount < 0 ? '' : '+'}\u20ac{(Math.abs(t.amount) / 100).toFixed(2)}
										</TableCell>
										<TableCell>
											<ResourceLink
												id={t.user_id}
												href={`/users/${t.user_id}`}
												name={t.users?.display_name || t.users?.phone_number}
											/>
										</TableCell>
										<TableCell className="max-w-xs truncate text-zinc-500">{t.reason || '\u2014'}</TableCell>
										<TableCell className="text-zinc-500">{formatDate(t.created_at)}</TableCell>
									</TableRow>
								))}
							</TableBody>
						</Table>
					</section>
				</>
			)}
			<Divider className="my-6" />

			{/* Order Progress */}
			<section className="mb-6">
				<Subheading className="mb-4">Order Progress</Subheading>
				<OrderProgress status={order.status} />
			</section>

			<Divider className="my-6" />

			{/* Order Slots */}
			<section className="mb-6">
				<Subheading className="mb-4">Order Slots</Subheading>
				{slotsLoading ? (
					<p className="text-sm text-zinc-500">Loading slots...</p>
				) : slots.length === 0 ? (
					<p className="text-sm text-zinc-500">No slots recorded.</p>
				) : (
					<Table dense>
						<TableHead>
							<TableRow>
								<TableHeader>Slot Name</TableHeader>
								<TableHeader>Value</TableHeader>
								<TableHeader>Confirmed</TableHeader>
								<TableHeader>Confidence</TableHeader>
								<TableHeader>Source</TableHeader>
								<TableHeader>Updated</TableHeader>
							</TableRow>
						</TableHead>
						<TableBody>
							{slots.map((slot) => (
								<TableRow key={slot.id}>
									<TableCell>
										<span className={requiredSlotNames.has(slot.slot_name) ? 'font-medium' : ''}>
											{SLOT_LABELS[slot.slot_name] || slot.slot_name}
										</span>
										{requiredSlotNames.has(slot.slot_name) && (
											<span className="ml-1 text-xs text-amber-600 dark:text-amber-400">*</span>
										)}
									</TableCell>
									<TableCell className="max-w-xs">
										<div className="truncate">{formatSlotValue(slot.slot_name, slot.value, slotMap)}</div>
										{slot.slot_name === 'front_image_asset_id' && slot.value && (() => {
											const aid = (slot.value as any).asset_id ?? (slot.value as any).id;
										return aid ? <ImageThumbnail assetId={aid} alt="Front image" height={48} className="mt-1 rounded" /> : null;
										})()}
									</TableCell>
									<TableCell>
										{slot.confirmed ? (
											<Check className="h-4 w-4 text-green-600" />
										) : (
											<X className="h-4 w-4 text-zinc-400" />
										)}
									</TableCell>
									<TableCell>
										{slot.confidence !== null ? `${Math.round(slot.confidence * 100)}%` : '—'}
									</TableCell>
									<TableCell className="text-zinc-500">{slot.source}</TableCell>
									<TableCell className="text-zinc-500">{formatDate(slot.updated_at)}</TableCell>
								</TableRow>
							))}
						</TableBody>
					</Table>
				)}
			</section>

			{/* Preview Image */}
			{previewAsset && (
				<>
					<Divider className="my-6" />
					<section className="mb-6">
						<Subheading className="mb-4">Preview</Subheading>
						<ImageThumbnail assetId={previewAsset.id} alt="Postcard preview" height={300} className="rounded-lg" />
					</section>
				</>
			)}

			{/* Print Assets */}
			{assets.length > 0 && <OrderAssetsGrid assets={assets} />}

			<Divider className="my-6" />

			{/* Payment History */}
			<section className="mb-6">
				<Subheading className="mb-4">Payment History</Subheading>
				{paymentsLoading ? (
					<p className="text-sm text-zinc-500">Loading payments...</p>
				) : payments.length === 0 ? (
					<p className="text-sm text-zinc-500">No payment intents recorded.</p>
				) : (
					<Table dense>
						<TableHead>
							<TableRow>
								<TableHeader>Amount</TableHeader>
								<TableHeader>Currency</TableHeader>
								<TableHeader>Status</TableHeader>
								<TableHeader>Stripe ID</TableHeader>
								<TableHeader>Created</TableHeader>
							</TableRow>
						</TableHead>
						<TableBody>
							{payments.map((pi) => (
								<TableRow key={pi.id}>
									<TableCell className="font-medium">
										{formatCents(pi.amount, pi.currency)}
									</TableCell>
									<TableCell className="uppercase">{pi.currency}</TableCell>
									<TableCell>
										<PaymentStatusBadge status={pi.status} />
									</TableCell>
									<TableCell className="font-mono text-xs text-zinc-500">
										{pi.stripe_payment_intent_id}
									</TableCell>
									<TableCell className="text-zinc-500">{formatDate(pi.created_at)}</TableCell>
								</TableRow>
							))}
						</TableBody>
					</Table>
				)}
			</section>

			<Divider className="my-6" />

			{/* Fulfillment */}
			<section className="mb-6">
				<Subheading className="mb-4">Fulfillment</Subheading>
				{prodigiLoading || lobLoading ? (
					<p className="text-sm text-zinc-500">Loading fulfillment data...</p>
				) : prodigiOrders.length === 0 && lobOrders.length === 0 ? (
					<p className="text-sm text-zinc-500">No fulfillment orders recorded.</p>
				) : (
					<div className="space-y-6">
						{prodigiOrders.length > 0 && (
							<div>
								<p className="mb-2 text-sm font-medium text-zinc-500">Prodigi</p>
								<Table dense>
									<TableHead>
										<TableRow>
											<TableHeader>Prodigi ID</TableHeader>
											<TableHeader>Status</TableHeader>
											<TableHeader>Tracking</TableHeader>
											<TableHeader>Carrier</TableHeader>
											<TableHeader>Created</TableHeader>
										</TableRow>
									</TableHead>
									<TableBody>
										{prodigiOrders.map((po) => (
											<TableRow key={po.id}>
												<TableCell className="font-mono text-sm">{po.prodigi_order_id}</TableCell>
												<TableCell>
													<ProdigiStatusBadge status={po.status} />
												</TableCell>
												<TableCell>
													{po.tracking_url ? (
														<Link
															href={po.tracking_url}
															className="text-blue-600 hover:text-blue-800 dark:text-blue-400"
														>
															{po.tracking_number || 'Track'}
														</Link>
													) : (
														<span className="text-zinc-400">{po.tracking_number || '—'}</span>
													)}
												</TableCell>
												<TableCell className="text-zinc-500">{po.carrier || '—'}</TableCell>
												<TableCell className="text-zinc-500">{formatDate(po.created_at)}</TableCell>
											</TableRow>
										))}
									</TableBody>
								</Table>
							</div>
						)}
						{lobOrders.length > 0 && (
							<div>
								<p className="mb-2 text-sm font-medium text-zinc-500">Lob</p>
								<Table dense>
									<TableHead>
										<TableRow>
											<TableHeader>Lob Postcard ID</TableHeader>
											<TableHeader>Status</TableHeader>
											<TableHeader>Expected Delivery</TableHeader>
											<TableHeader>Carrier</TableHeader>
											<TableHeader>Created</TableHeader>
										</TableRow>
									</TableHead>
									<TableBody>
										{lobOrders.map((lo) => (
											<TableRow key={lo.id}>
												<TableCell className="font-mono text-sm">{lo.lob_postcard_id}</TableCell>
												<TableCell>
													<LobStatusBadge status={lo.status as LobStatus} />
												</TableCell>
												<TableCell className="text-zinc-500">
													{lo.expected_delivery_date
														? new Date(lo.expected_delivery_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
														: '—'}
												</TableCell>
												<TableCell className="text-zinc-500">{lo.carrier || '—'}</TableCell>
												<TableCell className="text-zinc-500">{formatDate(lo.created_at)}</TableCell>
											</TableRow>
										))}
									</TableBody>
								</Table>
							</div>
						)}
					</div>
				)}
			</section>

			<Divider className="my-6" />

			{/* Chat Link */}
			{order.user_id && (
				<section>
					<Button href={`/chats/${order.user_id}`} outline>
						<MessageSquare className="h-4 w-4" />
						View Conversation
					</Button>
				</section>
			)}
		</div>
	);
}
